import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import MyDogDocuments from './MyDogDocuments';
import ConfirmationModal from './ConfirmationModal';
import {
  PlusIcon,
  CameraIcon,
  TrashIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { FaDog } from 'react-icons/fa';
import { format, differenceInYears } from 'date-fns';
import { de } from 'date-fns/locale';

const CARD_BG = '#1c2432';

// Typed icon
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

interface Dog {
  id?: string;
  hunde_id: string;
  hundename: string;
  rufname?: string;
  vollstaendiger_name?: string;
  rasse?: string;
  geburtsdatum?: string;
  geburtstag?: string;
  geschlecht?: string | number | null;
  kastriert?: boolean | string | number | null;
  chip_nummer?: string;
  chipnummer?: string;
  pass_nr?: string;
  tassonummer?: string;
  tollwut?: string;
  tollwut_impfung_ablauf?: string;
  impfstatus_tollwut?: string;
  valid_tollwut?: boolean;
  versicherung?: string;
  insurance_name?: string;
  valid_insurance?: boolean;
  foto_path?: string;
  image_path?: string;
  image_path_updated?: number;
  imageTimestamp?: number;
  problems?: string[];
  hundebox?: string | number | boolean | null;
  extras?: string;
}

interface DogModalProps {
  dog?: Dog;
  isOpen: boolean;
  onClose: () => void;
  onSave: (message?: string) => void;
  onDelete?: () => void;
}

// =============================================================================
// DOG MODAL — Dark Story Style
// =============================================================================
const DogModal: React.FC<DogModalProps> = ({ dog, isOpen, onClose, onSave, onDelete }) => {
  const { BASE_URL } = useContext(AuthContext);
  const [formData, setFormData] = useState<any>({
    rufname: '',
    rasse: '',
    geburtstag: '',
    geschlecht: 'Männlich',
    kastriert: '',
    chipnummer: '',
    tassonummer: '',
    tollwut_impfung_ablauf: '',
    insurance_name: '',
    hundebox: '',
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageLoadError, setImageLoadError] = useState(false);

  useEffect(() => {
    if (dog) {
      const birthDate = dog.geburtstag || dog.geburtsdatum;
      const tollwutDate = dog.tollwut_impfung_ablauf || dog.impfstatus_tollwut || dog.tollwut;
      setFormData({
        rufname: dog.rufname || dog.hundename || '',
        rasse: dog.rasse || '',
        geburtstag: birthDate ? birthDate.split('T')[0] : '',
        geschlecht:
          dog.geschlecht === 1 || dog.geschlecht === 'weiblich' || dog.geschlecht === 'Weiblich'
            ? 'Weiblich'
            : dog.geschlecht === 0 || dog.geschlecht === 'männlich' || dog.geschlecht === 'Männlich'
            ? 'Männlich'
            : '',
        kastriert:
          dog.kastriert === true ||
          dog.kastriert === 1 ||
          dog.kastriert === 'true' ||
          dog.kastriert === 'Ja' ||
          dog.kastriert === 'ja'
            ? 'ja'
            : dog.kastriert === false ||
              dog.kastriert === 0 ||
              dog.kastriert === 'false' ||
              dog.kastriert === 'Nein' ||
              dog.kastriert === 'nein'
            ? 'nein'
            : '',
        chipnummer: dog.chipnummer || dog.chip_nummer || '',
        tassonummer: dog.tassonummer || dog.pass_nr || '',
        tollwut_impfung_ablauf: tollwutDate ? tollwutDate.split('T')[0] : '',
        insurance_name: dog.insurance_name || dog.versicherung || '',
        hundebox:
          dog.hundebox === 1 ||
          dog.hundebox === true ||
          dog.hundebox === 'ja' ||
          dog.hundebox === 'Ja'
            ? 'ja'
            : dog.hundebox === 0 ||
              dog.hundebox === false ||
              dog.hundebox === 'nein' ||
              dog.hundebox === 'Nein'
            ? 'nein'
            : '',
      });
      if (dog.foto_path || dog.image_path) {
        setImagePreview(
          `${BASE_URL}/api/dogs/image-url/${dog.hunde_id || dog.id}?token=${localStorage.getItem(
            'jwt_token'
          )}&ts=${dog.image_path_updated || Date.now()}`
        );
        setImageLoadError(false);
      }
    } else {
      setFormData({
        rufname: '',
        rasse: '',
        geburtstag: '',
        geschlecht: 'Männlich',
        kastriert: '',
        chipnummer: '',
        tassonummer: '',
        tollwut_impfung_ablauf: '',
        insurance_name: '',
        hundebox: '',
      });
      setImagePreview(null);
      setImageLoadError(false);
    }
    setImageFile(null);
  }, [dog, isOpen, BASE_URL]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImageLoadError(false);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach((key) => {
        const value = formData[key];
        if (dog && (!value || value === '')) return;
        if (key === 'kastriert') {
          if (value === 'ja') submitData.append(key, '1');
          else if (value === 'nein') submitData.append(key, '0');
          else if (!dog) submitData.append(key, '');
        } else if (key === 'geschlecht') {
          if (value === 'Männlich') submitData.append(key, '0');
          else if (value === 'Weiblich') submitData.append(key, '1');
          else if (!dog) submitData.append(key, '');
        } else if (key === 'hundebox') {
          if (value === 'ja' || value === 'Ja') submitData.append(key, '1');
          else if (value === 'nein' || value === 'Nein') submitData.append(key, '0');
          else if (!dog) submitData.append(key, '');
        } else {
          if (!dog || value) submitData.append(key, value || '');
        }
      });
      if (imageFile) submitData.append('image', imageFile);

      const endpoint = dog ? `/api/dogs/${dog.hunde_id}` : '/api/dogs/add_dog';
      const response = await apiClient[dog ? 'put' : 'post'](endpoint, submitData, {
        headers: { 'Content-Type': undefined as any },
      });
      if (response.data.success) {
        onSave(dog ? 'Hund erfolgreich aktualisiert' : 'Hund erfolgreich hinzugefügt');
        onClose();
      } else {
        setError(response.data.message || 'Ein Fehler ist aufgetreten');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ein Netzwerkfehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const calculateAgeStr = (birthDate: string) => {
    if (!birthDate) return '';
    const birth = new Date(birthDate);
    const today = new Date();
    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    if (months < 0 || (months === 0 && today.getDate() < birth.getDate())) {
      years--;
      months = months < 0 ? 12 + months : 11;
    }
    if (years === 0 && months === 0) return 'Welpe';
    if (years === 0) return `${months} ${months === 1 ? 'Monat' : 'Monate'}`;
    return `${years} ${years === 1 ? 'Jahr' : 'Jahre'}`;
  };

  const inputClass =
    'mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-white outline-none transition';
  const inputStyle = {
    background: 'rgba(15,23,42,0.6)',
    border: '1px solid #334155',
    colorScheme: 'dark' as const,
  };
  const inputStyleFocused = {
    background: 'rgba(15,23,42,0.6)',
    border: '1px solid rgba(16,185,129,0.40)',
    colorScheme: 'dark' as const,
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-3xl flex flex-col overflow-hidden"
        style={{
          background: CARD_BG,
          border: '1px solid #334155',
          boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)',
          maxHeight: 'calc(100vh - 32px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-slate-800">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-primary-300 font-bold">
              {dog ? 'Bearbeiten' : 'Neuer Hund'}
            </div>
            <h2 className="text-2xl font-black text-white tracking-tight mt-1">
              {dog ? `${formData.rufname || 'Hund'} bearbeiten` : 'Hund anlegen'}
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              3 Sektionen · Pflichtfelder mit *
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center transition"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.10)',
            }}
            aria-label="Schließen"
          >
            <XMarkIcon className="w-4 h-4 text-slate-300" />
          </button>
        </div>

        {/* Body — split, both columns scroll together */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="grid sm:grid-cols-[5fr_7fr] flex-1 overflow-y-auto items-start">
            {/* Photo upload */}
            <div className="relative aspect-[4/5] sm:aspect-auto sm:min-h-[420px] overflow-hidden bg-slate-900/40 flex items-center justify-center border-b sm:border-b-0 sm:border-r border-slate-800">
              {imagePreview && !imageLoadError ? (
                <>
                  <img
                    src={imagePreview}
                    alt={formData.rufname || 'Hund'}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={() => setImageLoadError(true)}
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(2,6,23,0.10) 0%, rgba(2,6,23,0) 30%, rgba(2,6,23,0.55) 70%, rgba(28,36,50,0.95) 100%)',
                    }}
                  />
                  <div className="relative text-center px-6 z-10 mt-auto pb-6">
                    {formData.rufname && (
                      <h3
                        className="text-white font-black"
                        style={{
                          fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
                          letterSpacing: '-0.04em',
                          textShadow: '0 1px 2px rgba(0,0,0,0.55)',
                        }}
                      >
                        {formData.rufname}
                      </h3>
                    )}
                    {(formData.rasse || formData.geburtstag) && (
                      <p
                        className="text-white/95 text-sm mt-1"
                        style={{ textShadow: '0 1px 2px rgba(0,0,0,0.55)' }}
                      >
                        {formData.rasse || ''}
                        {formData.geburtstag && (
                          <>
                            {formData.rasse ? ' · ' : ''}
                            {calculateAgeStr(formData.geburtstag)}
                          </>
                        )}
                      </p>
                    )}
                  </div>
                  <label className="absolute top-4 right-4 z-20 cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                    <div
                      className="w-10 h-10 rounded-xl backdrop-blur-md flex items-center justify-center text-slate-200 transition"
                      style={{
                        background: 'rgba(2,6,23,0.70)',
                        border: '1px solid rgba(255,255,255,0.10)',
                      }}
                    >
                      <CameraIcon className="w-4 h-4" />
                    </div>
                  </label>
                </>
              ) : (
                <div className="text-center px-6">
                  <div className="mx-auto w-20 h-20 rounded-2xl bg-primary-500/10 border border-primary-500/30 flex items-center justify-center mb-4">
                    <CameraIcon className="w-10 h-10 text-primary-400" />
                  </div>
                  <p className="text-sm font-bold text-white">Foto hochladen</p>
                  <p className="text-[11px] text-slate-400 mt-1">Optional · max. 5 MB</p>
                  <label className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-slate-200 hover:bg-white/10 text-xs font-semibold transition cursor-pointer"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.10)',
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                    Foto auswählen
                  </label>
                </div>
              )}
            </div>

            {/* Form fields */}
            <div className="p-6 space-y-5">
              {error && (
                <div className="p-3 rounded-xl flex items-center gap-2 text-sm border border-rose-500/30 bg-rose-500/10 text-rose-200">
                  <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Section: Basisdaten */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-primary-300 font-bold mb-3">
                  Basisdaten
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      Rufname *
                    </span>
                    <input
                      type="text"
                      required
                      value={formData.rufname}
                      onChange={(e) => setFormData({ ...formData, rufname: e.target.value })}
                      placeholder="Max"
                      className={inputClass}
                      style={formData.rufname ? inputStyleFocused : inputStyle}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      Rasse
                    </span>
                    <input
                      type="text"
                      value={formData.rasse}
                      onChange={(e) => setFormData({ ...formData, rasse: e.target.value })}
                      placeholder="Labrador"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      Geburtsdatum
                    </span>
                    <input
                      type="date"
                      value={formData.geburtstag}
                      onChange={(e) => setFormData({ ...formData, geburtstag: e.target.value })}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      Geschlecht
                    </span>
                    <select
                      value={formData.geschlecht}
                      onChange={(e) => setFormData({ ...formData, geschlecht: e.target.value })}
                      className={inputClass}
                      style={inputStyle}
                    >
                      <option value="">Bitte wählen…</option>
                      <option value="Männlich">Männlich</option>
                      <option value="Weiblich">Weiblich</option>
                    </select>
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      Kastriert
                    </span>
                    <select
                      value={formData.kastriert || ''}
                      onChange={(e) => setFormData({ ...formData, kastriert: e.target.value })}
                      className={inputClass}
                      style={inputStyle}
                    >
                      <option value="">Bitte wählen…</option>
                      <option value="ja">Ja</option>
                      <option value="nein">Nein</option>
                    </select>
                  </label>
                </div>
              </div>

              {/* Section: Registrierung */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-primary-300 font-bold mb-3">
                  Registrierung
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      Chipnummer
                    </span>
                    <input
                      type="text"
                      value={formData.chipnummer}
                      onChange={(e) => setFormData({ ...formData, chipnummer: e.target.value })}
                      placeholder="276…"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      Tasso-Nummer
                    </span>
                    <input
                      type="text"
                      value={formData.tassonummer}
                      onChange={(e) => setFormData({ ...formData, tassonummer: e.target.value })}
                      placeholder="A-…"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </label>
                </div>
              </div>

              {/* Section: Schutz */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-primary-300 font-bold mb-3">
                  Tollwut · Versicherung · Extras
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      Tollwut gültig bis
                    </span>
                    <input
                      type="date"
                      value={formData.tollwut_impfung_ablauf}
                      onChange={(e) =>
                        setFormData({ ...formData, tollwut_impfung_ablauf: e.target.value })
                      }
                      className={inputClass}
                      style={inputStyle}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      Versicherung
                    </span>
                    <input
                      type="text"
                      value={formData.insurance_name}
                      onChange={(e) =>
                        setFormData({ ...formData, insurance_name: e.target.value })
                      }
                      placeholder="HUK · Allianz · Uelzener"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                      Hundebox vorhanden
                    </span>
                    <select
                      value={formData.hundebox || ''}
                      onChange={(e) => setFormData({ ...formData, hundebox: e.target.value })}
                      className={inputClass}
                      style={inputStyle}
                    >
                      <option value="">Bitte wählen…</option>
                      <option value="ja">Ja</option>
                      <option value="nein">Nein</option>
                    </select>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:items-center gap-2"
            style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
          >
            {dog && onDelete && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onDelete();
                }}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl text-rose-300 text-sm font-semibold transition sm:mr-auto"
                style={{
                  background: 'rgba(244,63,94,0.10)',
                  border: '1px solid rgba(244,63,94,0.30)',
                }}
              >
                <TrashIcon className="w-4 h-4" />
                Hund löschen
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-3 rounded-xl text-slate-200 text-sm font-semibold transition"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-sm font-bold transition disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-slate-950 border-t-transparent rounded-full" />
                  Speichert…
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-4 h-4" />
                  {dog ? 'Speichern' : 'Hinzufügen'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// MY DOGS — Main Component
// =============================================================================
const MyDogs: React.FC = () => {
  const { BASE_URL } = useContext(AuthContext);
  const [dogs, setDogs] = useState<Dog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDog, setEditingDog] = useState<Dog | undefined>(undefined);
  const [expandedDog, setExpandedDog] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: 'info' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
  });
  const [dogDocumentStatus, setDogDocumentStatus] = useState<{
    [key: string]: { missingDocuments: any[]; isVaccinationExpired: boolean };
  }>({});

  const checkDogDocuments = useCallback(async (dogId: string) => {
    try {
      const response = await apiClient.get(`/api/dogs/check_required_documents/${dogId}`);
      return {
        missingDocuments: response.data.missing_documents || [],
        success: response.data.success,
      };
    } catch (error) {
      console.error('Error checking dog documents:', error);
      return { missingDocuments: [], success: true };
    }
  }, []);

  const checkVaccinationExpiry = useCallback((vaccinationDate: string | undefined) => {
    if (!vaccinationDate) return false;
    const vaccDate = new Date(vaccinationDate);
    const today = new Date();
    return vaccDate < today;
  }, []);

  const loadDogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/dogs/get_user_dogs');
      if (response.data.success) {
        const dogsData = (response.data.dogs || []).map((dog: any) => {
          let kastriertValue = null;
          const rawValue = dog.kastriert;
          if (
            rawValue === true ||
            rawValue === 1 ||
            rawValue === 'Ja' ||
            String(rawValue).toLowerCase() === 'true'
          ) {
            kastriertValue = true;
          } else if (
            rawValue === false ||
            rawValue === 0 ||
            rawValue === 'Nein' ||
            String(rawValue).toLowerCase() === 'false'
          ) {
            kastriertValue = false;
          }
          const isVaccinationExpired = checkVaccinationExpiry(
            dog.tollwut_impfung_ablauf || dog.impfstatus_tollwut
          );
          return {
            ...dog,
            hunde_id: dog.hunde_id || dog.id,
            id: dog.id || dog.hunde_id,
            hundename: dog.hundename || dog.rufname,
            rufname: dog.rufname || dog.hundename,
            geburtsdatum: dog.geburtsdatum || dog.geburtstag,
            geburtstag: dog.geburtstag || dog.geburtsdatum,
            foto_path: dog.foto_path || dog.image_path,
            image_path: dog.image_path || dog.foto_path,
            kastriert: kastriertValue,
            imageTimestamp: dog.imageTimestamp || dog.image_path_updated || Date.now(),
            image_path_updated: dog.image_path_updated || dog.imageTimestamp || Date.now(),
            isVaccinationExpired,
          };
        });
        setDogs(dogsData);

        const statusChecks = dogsData.map(async (dog: any) => {
          const docStatus = await checkDogDocuments(dog.id || dog.hunde_id);
          return {
            dogId: dog.id || dog.hunde_id,
            missingDocuments: docStatus.missingDocuments,
            isVaccinationExpired: dog.isVaccinationExpired,
          };
        });
        const allStatuses = await Promise.all(statusChecks);
        const statusMap = allStatuses.reduce((acc, status) => {
          acc[status.dogId] = {
            missingDocuments: status.missingDocuments,
            isVaccinationExpired: status.isVaccinationExpired,
          };
          return acc;
        }, {} as { [key: string]: { missingDocuments: any[]; isVaccinationExpired: boolean } });
        setDogDocumentStatus(statusMap);
      }
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Error loading dogs:', error);
        showNotification('Fehler beim Laden der Hunde', 'error');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkDogDocuments, checkVaccinationExpiry]);

  useEffect(() => {
    loadDogs();
  }, [loadDogs]);

  const showNotification = (message: string, type: 'success' | 'error') => {
    setConfirmModal({
      visible: true,
      type,
      title: type === 'success' ? 'Erfolgreich' : 'Fehler',
      message,
      variant: 'single',
      onConfirm: () => {},
    });
  };

  const handleImageUpload = async (dogId: string, file: File) => {
    setUploadingImage(dogId);
    const currentDog = dogs.find((d) => d.hunde_id === dogId);
    if (!currentDog) {
      showNotification('Hund nicht gefunden', 'error');
      setUploadingImage(null);
      return;
    }
    const formData = new FormData();
    formData.append('rufname', currentDog.rufname || currentDog.hundename || '');
    formData.append('vollstaendiger_name', currentDog.vollstaendiger_name || '');
    formData.append('rasse', currentDog.rasse || '');
    formData.append('geburtstag', currentDog.geburtstag || currentDog.geburtsdatum || '');
    formData.append('chipnummer', currentDog.chipnummer || currentDog.chip_nummer || '');
    formData.append('tassonummer', currentDog.tassonummer || currentDog.pass_nr || '');
    formData.append(
      'tollwut_impfung_ablauf',
      currentDog.tollwut_impfung_ablauf ||
        currentDog.impfstatus_tollwut ||
        currentDog.tollwut ||
        ''
    );
    formData.append('insurance_name', currentDog.insurance_name || currentDog.versicherung || '');
    if (
      currentDog.geschlecht === 1 ||
      currentDog.geschlecht === 'weiblich' ||
      currentDog.geschlecht === 'Weiblich'
    ) {
      formData.append('geschlecht', '1');
    } else if (
      currentDog.geschlecht === 0 ||
      currentDog.geschlecht === 'männlich' ||
      currentDog.geschlecht === 'Männlich'
    ) {
      formData.append('geschlecht', '0');
    } else {
      formData.append('geschlecht', '');
    }
    if (
      currentDog.kastriert === true ||
      currentDog.kastriert === 1 ||
      currentDog.kastriert === 'ja' ||
      currentDog.kastriert === 'Ja' ||
      currentDog.kastriert === '1' ||
      currentDog.kastriert === 'true'
    ) {
      formData.append('kastriert', '1');
    } else if (
      currentDog.kastriert === false ||
      currentDog.kastriert === 0 ||
      currentDog.kastriert === 'nein' ||
      currentDog.kastriert === 'Nein' ||
      currentDog.kastriert === '0' ||
      currentDog.kastriert === 'false'
    ) {
      formData.append('kastriert', '0');
    } else {
      formData.append('kastriert', '');
    }
    if (
      currentDog.hundebox === 1 ||
      currentDog.hundebox === true ||
      currentDog.hundebox === 'ja' ||
      currentDog.hundebox === 'Ja'
    ) {
      formData.append('hundebox', '1');
    } else if (
      currentDog.hundebox === 0 ||
      currentDog.hundebox === false ||
      currentDog.hundebox === 'nein' ||
      currentDog.hundebox === 'Nein'
    ) {
      formData.append('hundebox', '0');
    } else {
      formData.append('hundebox', '');
    }
    formData.append('image', file);

    try {
      const response = await apiClient.put(`/api/dogs/${dogId}`, formData, {
        headers: { 'Content-Type': undefined as any },
      });
      if (response.data.success) {
        showNotification('Foto erfolgreich hochgeladen', 'success');
        loadDogs();
      } else {
        showNotification(response.data.message || 'Fehler beim Hochladen des Fotos', 'error');
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      showNotification(
        error.response?.data?.message || 'Fehler beim Hochladen des Fotos',
        'error'
      );
    } finally {
      setUploadingImage(null);
    }
  };

  const handleDeleteDog = async (dogId: string, dogName: string) => {
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Hund löschen?',
      message: `Möchten Sie ${dogName} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      variant: 'two',
      onConfirm: async () => {
        try {
          const response = await apiClient.delete(`/api/dogs/${dogId}`);
          if (response.data.success) {
            showNotification(`${dogName} wurde erfolgreich gelöscht`, 'success');
            loadDogs();
          }
        } catch (error) {
          console.error('Error deleting dog:', error);
          showNotification('Fehler beim Löschen des Hundes', 'error');
        }
      },
    });
  };

  const formatDateShort = (dateString: string | undefined) => {
    if (!dateString) return null;
    try {
      return format(new Date(dateString), 'dd.MM.yyyy', { locale: de });
    } catch {
      return null;
    }
  };

  const formatDateLong = (dateString: string | undefined) => {
    if (!dateString) return null;
    try {
      return format(new Date(dateString), 'dd. MMMM yyyy', { locale: de });
    } catch {
      return null;
    }
  };

  const calculateAge = (birthDate: string | undefined) => {
    if (!birthDate) return null;
    try {
      const years = differenceInYears(new Date(), new Date(birthDate));
      if (years === 0) return 'Welpe';
      return `${years} ${years === 1 ? 'Jahr' : 'Jahre'}`;
    } catch {
      return null;
    }
  };

  const daysUntil = (dateString: string | undefined) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      const today = new Date();
      const diff = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return diff;
    } catch {
      return null;
    }
  };

  const calculateProfileCompletion = (dog: Dog, dogStatus: any) => {
    const rufname = dog.rufname || dog.hundename;
    const geburtstag = dog.geburtstag || dog.geburtsdatum;
    const geschlecht = dog.geschlecht;
    const tollwut = dog.tollwut_impfung_ablauf || dog.impfstatus_tollwut || dog.tollwut;
    const hundebox = dog.hundebox;
    const kastriert =
      dog.kastriert !== null && dog.kastriert !== undefined && dog.kastriert !== '';
    const versicherung = dog.versicherung || dog.insurance_name;
    const pflichtdokumente = dogStatus.missingDocuments.length === 0;
    const requiredFields = [
      rufname,
      geburtstag,
      geschlecht,
      tollwut,
      hundebox,
      kastriert,
      versicherung,
      pflichtdokumente,
    ];
    const completedFields = requiredFields.filter((field) => Boolean(field));
    return Math.round((completedFields.length / requiredFields.length) * 100);
  };

  // ============================================================
  // DOG CARD — Story Hero Split Layout
  // ============================================================
  const DogCard: React.FC<{ dog: Dog }> = ({ dog }) => {
    const isExpanded = expandedDog === dog.hunde_id;
    const dogStatus = dogDocumentStatus[dog.id || dog.hunde_id] || {
      missingDocuments: [],
      isVaccinationExpired: false,
    };
    const completionPct = calculateProfileCompletion(dog, dogStatus);
    const completionDeg = (completionPct / 100) * 360;
    const completionColor =
      completionPct >= 90 ? '#34d399' : completionPct >= 70 ? '#fbbf24' : '#f87171';
    const isComplete = completionPct === 100;

    const [imageError, setImageError] = useState(false);
    const dogImageUrl =
      (dog.foto_path || dog.image_path) && !imageError
        ? `${BASE_URL}/api/dogs/image-url/${
            dog.hunde_id || dog.id
          }?token=${localStorage.getItem('jwt_token')}&ts=${
            dog.image_path_updated || dog.imageTimestamp || Date.now()
          }`
        : null;

    const dogName = dog.rufname || dog.hundename || 'Unbenannt';
    const dogBreed = dog.rasse;
    const ageStr = calculateAge(dog.geburtstag || dog.geburtsdatum);
    const tollwutDate = dog.tollwut_impfung_ablauf || dog.impfstatus_tollwut || dog.tollwut;
    const tollwutDays = daysUntil(tollwutDate);
    const versicherung = dog.versicherung || dog.insurance_name;
    const chipNr = dog.chip_nummer || dog.chipnummer;
    const tassoNr = dog.pass_nr || dog.tassonummer;
    const hundeboxJa = dog.hundebox === 1 || String(dog.hundebox).toLowerCase() === 'ja';
    const hundeboxNein = dog.hundebox === 0 || String(dog.hundebox).toLowerCase() === 'nein';

    const hasProblems =
      dogStatus.missingDocuments.length > 0 || dogStatus.isVaccinationExpired;
    const tollwutWarning = tollwutDays !== null && tollwutDays >= 0 && tollwutDays <= 30;
    const tollwutExpired = dogStatus.isVaccinationExpired;
    const problems: string[] = [];
    if (tollwutExpired) problems.push('Tollwut-Auffrischung überfällig');
    if (tollwutWarning && !tollwutExpired)
      problems.push(`Tollwut läuft in ${tollwutDays} Tagen ab`);
    dogStatus.missingDocuments.forEach((d: any) => problems.push(d.description));

    return (
      <article
        className="group relative rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
        style={{
          boxShadow:
            '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)',
        }}
      >
        {/* Split grid */}
        <div className="grid md:grid-cols-[6fr_7fr]">
          {/* LEFT: Photo */}
          <div className="relative overflow-hidden aspect-[4/5] md:aspect-auto md:min-h-[520px]">
            {dogImageUrl ? (
              <img
                src={dogImageUrl}
                alt={dogName}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                style={{ objectPosition: 'center 30%' }}
                onError={() => setImageError(true)}
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(16,185,129,0.20) 0%, rgba(2,6,23,0.40) 100%)',
                }}
              >
                <DogIcon className="text-white/40 h-32 w-32" />
              </div>
            )}

            {/* Mobile gradient */}
            <div
              className="absolute inset-0 pointer-events-none md:hidden"
              style={{
                background:
                  'linear-gradient(180deg, rgba(2,6,23,0.05) 0%, rgba(2,6,23,0) 30%, rgba(2,6,23,0.55) 65%, rgba(28,36,50,0.98) 100%)',
              }}
            />
            {/* Desktop vignette */}
            <div
              className="hidden md:block absolute inset-0 pointer-events-none"
              style={{
                background:
                  'linear-gradient(180deg, rgba(2,6,23,0.30) 0%, rgba(2,6,23,0) 18%, rgba(2,6,23,0) 78%, rgba(2,6,23,0.45) 100%)',
              }}
            />

            {/* Mobile pills top */}
            <div className="md:hidden absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-5 pt-5">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold backdrop-blur"
                  style={{
                    color: hasProblems ? '#fde68a' : isComplete ? '#a7f3d0' : '#fde68a',
                    background: hasProblems
                      ? 'rgba(245,158,11,0.12)'
                      : isComplete
                      ? 'rgba(16,185,129,0.12)'
                      : 'rgba(245,158,11,0.12)',
                    border: `1px solid ${
                      hasProblems
                        ? 'rgba(245,158,11,0.40)'
                        : isComplete
                        ? 'rgba(16,185,129,0.40)'
                        : 'rgba(245,158,11,0.40)'
                    }`,
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full animate-pulse"
                    style={{
                      background: hasProblems ? '#fbbf24' : isComplete ? '#34d399' : '#fbbf24',
                    }}
                  />
                  {completionPct} %
                </span>
                {hasProblems && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-amber-500/40 bg-amber-500/15 text-amber-200 backdrop-blur">
                    Action nötig
                  </span>
                )}
              </div>
            </div>

            {/* Mobile bottom: name + meta */}
            <div className="md:hidden absolute inset-x-0 bottom-0 px-5 pb-5 z-10">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {dogBreed && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white/20 bg-white/10 text-white backdrop-blur">
                    {dogBreed}
                    {ageStr && ` · ${ageStr}`}
                  </span>
                )}
                {!hasProblems && isComplete && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-primary-400/30 bg-primary-500/15 text-primary-300 backdrop-blur">
                    Vollständig
                  </span>
                )}
              </div>
              <h3
                className="text-white font-black leading-none"
                style={{
                  fontSize: 'clamp(2rem, 7vw, 2.5rem)',
                  letterSpacing: '-0.04em',
                  textShadow: '0 1px 2px rgba(0,0,0,0.55), 0 4px 18px rgba(0,0,0,0.55)',
                }}
              >
                {dogName}
              </h3>
              {tollwutWarning && !tollwutExpired && (
                <p
                  className="text-amber-200 text-sm font-bold mt-2"
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.55)' }}
                >
                  Tollwut läuft in {tollwutDays} Tagen ab
                </p>
              )}
            </div>

            {/* Desktop floating: chip badge + actions */}
            <div className="hidden md:flex absolute bottom-5 left-5 right-5 items-end justify-between gap-3 z-10">
              {chipNr ? (
                <div
                  className="flex items-center gap-2.5 px-3 py-2 rounded-2xl backdrop-blur-md min-w-0"
                  style={{
                    background: 'rgba(2,6,23,0.70)',
                    border: '1px solid rgba(255,255,255,0.10)',
                  }}
                >
                  <div className="w-9 h-9 rounded-xl bg-primary-500/20 border border-primary-500/40 flex items-center justify-center shrink-0">
                    <DogIcon className="w-4 h-4 text-primary-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[9px] uppercase tracking-widest text-slate-300/90 font-bold">
                      Chip-Nr.
                    </div>
                    <div className="text-sm font-black text-white tabular-nums truncate">
                      {chipNr}
                    </div>
                  </div>
                </div>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => handleDeleteDog(dog.hunde_id, dogName)}
                  className="w-10 h-10 rounded-xl backdrop-blur-md flex items-center justify-center text-rose-300 transition"
                  style={{
                    background: 'rgba(2,6,23,0.70)',
                    border: '1px solid rgba(244,63,94,0.30)',
                  }}
                  title="Hund löschen"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
                <label
                  className="w-10 h-10 rounded-xl backdrop-blur-md flex items-center justify-center text-slate-200 transition cursor-pointer"
                  style={{
                    background: 'rgba(2,6,23,0.70)',
                    border: '1px solid rgba(255,255,255,0.10)',
                  }}
                  title="Foto wechseln"
                >
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(dog.hunde_id, file);
                    }}
                  />
                  {uploadingImage === dog.hunde_id ? (
                    <div className="animate-spin h-4 w-4 border-2 border-slate-200 border-t-transparent rounded-full" />
                  ) : (
                    <CameraIcon className="w-4 h-4" />
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* RIGHT: Content panel — desktop only */}
          <div className="relative hidden md:flex flex-col justify-between p-8 lg:p-10">
            {/* Top: pills */}
            <div className="flex items-start gap-2 flex-wrap">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                style={{
                  color: isComplete ? '#a7f3d0' : '#fde68a',
                  background: isComplete ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.12)',
                  border: `1px solid ${
                    isComplete ? 'rgba(16,185,129,0.40)' : 'rgba(245,158,11,0.40)'
                  }`,
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ background: isComplete ? '#34d399' : '#fbbf24' }}
                />
                Profil zu {completionPct} %
              </span>
              {dogBreed && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-gray-200 dark:border-white/15 bg-gray-100 dark:bg-slate-950/55 text-gray-800 dark:text-slate-200">
                  {dogBreed}
                  {ageStr && ` · ${ageStr}`}
                </span>
              )}
              {tollwutDate && !tollwutExpired && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-primary-400/30 bg-primary-500/15 text-primary-700 dark:text-primary-300">
                  Geimpft · gültig bis {formatDateShort(tollwutDate)}
                </span>
              )}
              {tollwutExpired && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300">
                  Tollwut abgelaufen
                </span>
              )}
              {versicherung && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300">
                  Versichert
                </span>
              )}
            </div>

            {/* Middle: Name + Story */}
            <div className="my-6 lg:my-8">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {(dog.kastriert === true || dog.kastriert === 1) && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-gray-200 dark:border-white/20 bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white">
                    Kastriert
                  </span>
                )}
                {hundeboxJa && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-gray-200 dark:border-white/20 bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white">
                    Hundebox ✓
                  </span>
                )}
                {hasProblems && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-300">
                    {problems.length} {problems.length === 1 ? 'Aufgabe' : 'Aufgaben'} offen
                  </span>
                )}
              </div>
              <h2
                className="text-primary-600 dark:text-primary-400 font-black tracking-tightest leading-[1.02]"
                style={{ fontSize: 'clamp(2.5rem, 4vw, 4.5rem)', letterSpacing: '-0.04em' }}
              >
                {dogName}
              </h2>
              <p className="text-gray-700 dark:text-slate-300 text-base lg:text-lg leading-relaxed mt-4 max-w-xl">
                {dog.geburtstag || dog.geburtsdatum ? (
                  <>
                    Geboren am{' '}
                    <span className="font-bold text-gray-900 dark:text-white">
                      {formatDateLong(dog.geburtstag || dog.geburtsdatum)}
                    </span>
                  </>
                ) : (
                  <>Geburtstag noch nicht eingetragen</>
                )}
                {tollwutWarning && !tollwutExpired && (
                  <>
                    {' · '}
                    <span className="font-bold text-amber-700 dark:text-amber-300">
                      Tollwut läuft in {tollwutDays} Tagen ab
                    </span>
                  </>
                )}
              </p>
            </div>

            {/* Bottom: stats + ring */}
            <div className="flex items-end justify-between gap-6">
              <div className="grid grid-cols-3 gap-4 flex-1">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                    Geburtstag
                  </div>
                  <div className="text-lg font-black text-gray-900 dark:text-white tabular-nums mt-0.5">
                    {formatDateShort(dog.geburtstag || dog.geburtsdatum) || '—'}
                  </div>
                  {ageStr && (
                    <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">{ageStr}</div>
                  )}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                    Tollwut
                  </div>
                  <div
                    className={`text-lg font-black tabular-nums mt-0.5 ${
                      tollwutExpired
                        ? 'text-rose-600 dark:text-rose-400'
                        : tollwutWarning
                        ? 'text-amber-600 dark:text-amber-400'
                        : tollwutDate
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-gray-500 dark:text-slate-500'
                    }`}
                  >
                    {tollwutDate ? formatDateShort(tollwutDate) : '—'}
                  </div>
                  {tollwutDays !== null && tollwutDays >= 0 && (
                    <div
                      className={`text-[10px] mt-0.5 ${
                        tollwutWarning ? 'text-amber-600/80 dark:text-amber-400/80' : 'text-gray-600 dark:text-slate-400'
                      }`}
                    >
                      {tollwutDays} Tage übrig
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                    Tasso-Nr.
                  </div>
                  <div className="text-lg font-black text-gray-900 dark:text-white tabular-nums mt-0.5 truncate">
                    {tassoNr || '—'}
                  </div>
                  {chipNr && (
                    <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5 truncate">
                      Chip {chipNr}
                    </div>
                  )}
                </div>
              </div>

              {/* Completion ring */}
              <div className="shrink-0">
                <div
                  className="relative w-24 h-24 rounded-full"
                  style={{
                    background: `conic-gradient(from 0deg, ${completionColor} 0deg ${completionDeg}deg, rgba(255,255,255,0.08) ${completionDeg}deg 360deg)`,
                    padding: 5,
                  }}
                >
                  <div
                    className="w-full h-full rounded-full flex flex-col items-center justify-center"
                    style={{ background: CARD_BG, border: '1px solid #1e293b' }}
                  >
                    <div className="text-[9px] uppercase tracking-widest text-slate-400">
                      Profil
                    </div>
                    <div className="text-2xl font-black text-white leading-none">
                      {completionPct}
                    </div>
                    <div
                      className="text-[9px] font-semibold mt-0.5"
                      style={{ color: completionColor }}
                    >
                      % komplett
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action banner if problems */}
        {hasProblems && (
          <div
            className="px-5 sm:px-8 py-4 border-t border-amber-500/30 bg-amber-500/5"
          >
            <div className="flex items-start gap-3 flex-wrap">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">
                  Action erforderlich
                </div>
                <ul className="mt-1 space-y-1 text-sm text-gray-700 dark:text-slate-300">
                  {problems.map((p, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-amber-400" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => setExpandedDog(isExpanded ? null : dog.hunde_id)}
                className="px-3 py-2 rounded-xl text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 text-xs font-bold transition bg-amber-500/10 border border-amber-500/30"
              >
                Dokumente ansehen
              </button>
            </div>
          </div>
        )}

        {/* Footer: details strip + CTAs */}
        <div className="relative px-5 sm:px-8 py-5 md:py-6 border-t border-gray-200 dark:border-slate-800">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-6 items-center">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Tollwut</div>
              <div
                className={`text-sm font-bold mt-1 ${
                  tollwutExpired
                    ? 'text-rose-600 dark:text-rose-400'
                    : tollwutWarning
                    ? 'text-amber-600 dark:text-amber-400'
                    : tollwutDate
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-gray-500 dark:text-slate-500'
                }`}
              >
                {tollwutDate ? formatDateShort(tollwutDate) : 'Fehlt'}
                {tollwutDate && (
                  <>
                    <br />
                    <span className="text-[11px] text-gray-600 dark:text-slate-400 font-medium">
                      {tollwutExpired
                        ? 'abgelaufen'
                        : tollwutDays !== null
                        ? `noch ${tollwutDays} Tage`
                        : ''}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                Versicherung
              </div>
              <div className="text-sm font-bold text-gray-900 dark:text-white mt-1 truncate" title={versicherung}>
                {versicherung || (
                  <span className="text-amber-600 dark:text-amber-400 font-medium italic">Fehlt</span>
                )}
              </div>
            </div>
            <div className="hidden md:block">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Tasso-Nr.</div>
              <div className="text-sm font-bold text-gray-900 dark:text-white mt-1 tabular-nums truncate">
                {tassoNr || '—'}
              </div>
            </div>
            <div className="hidden md:block">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Hundebox</div>
              <div className="text-sm font-bold text-gray-900 dark:text-white mt-1">
                {hundeboxJa ? 'Ja' : hundeboxNein ? 'Nein' : '—'}
              </div>
            </div>
            <div className="col-span-2 md:col-span-2 flex gap-2">
              <button
                onClick={() => setExpandedDog(isExpanded ? null : dog.hunde_id)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl text-gray-700 dark:text-slate-200 text-xs font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
              >
                Dokumente
                {isExpanded ? (
                  <ChevronUpIcon className="w-3.5 h-3.5" />
                ) : (
                  <ChevronDownIcon className="w-3.5 h-3.5" />
                )}
              </button>
              <button
                onClick={() => setEditingDog(dog)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-xs font-bold transition"
              >
                <PencilIcon className="w-3.5 h-3.5" />
                Bearbeiten
              </button>
            </div>
          </div>
        </div>

        {/* Documents — expanded */}
        {isExpanded && (
          <div className="px-5 sm:px-8 py-6 border-t border-gray-200 dark:border-slate-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-primary-500/10 border border-primary-500/30 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-primary-600 dark:text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <path d="M14 2v6h6" />
                </svg>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                  Dokumente · {dogName}
                </div>
                <h4 className="text-base font-black text-gray-900 dark:text-white">Impfpass · Versicherung · mehr</h4>
              </div>
            </div>
            <MyDogDocuments dogId={dog.id || dog.hunde_id} />
          </div>
        )}
      </article>
    );
  };

  // ============================================================
  // LOADING STATE
  // ============================================================
  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10 space-y-6">
          <div
            className="rounded-3xl p-8 animate-pulse bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
          >
            <div className="h-8 w-48 bg-gray-200 dark:bg-slate-700/60 rounded mb-3" />
            <div className="h-4 w-72 bg-gray-200 dark:bg-slate-700/40 rounded" />
          </div>
          {[1, 2].map((i) => (
            <div
              key={i}
              className="rounded-3xl p-8 animate-pulse bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div className="aspect-[4/5] bg-gray-200 dark:bg-slate-700/40 rounded-2xl" />
                <div className="space-y-3">
                  <div className="h-6 w-32 bg-gray-200 dark:bg-slate-700/60 rounded" />
                  <div className="h-12 w-48 bg-gray-200 dark:bg-slate-700/60 rounded" />
                  <div className="h-4 w-3/4 bg-gray-200 dark:bg-slate-700/40 rounded" />
                  <div className="h-4 w-1/2 bg-gray-200 dark:bg-slate-700/40 rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ============================================================
  // STATS + ACTIONS
  // ============================================================
  const totalDogs = dogs.length;

  // Generate story copy from real dog data
  const renderStorySnippet = (dog: Dog) => {
    const dogName = dog.rufname || dog.hundename || 'Hund';
    const status = dogDocumentStatus[dog.id || dog.hunde_id] || {
      missingDocuments: [],
      isVaccinationExpired: false,
    };
    const completionPct = calculateProfileCompletion(dog, status);
    const tollwutDate = dog.tollwut_impfung_ablauf || dog.impfstatus_tollwut || dog.tollwut;
    const tollwutDays = daysUntil(tollwutDate);

    if (status.isVaccinationExpired) {
      return (
        <>
          <span className="font-bold text-rose-700 dark:text-rose-300">{dogName}</span>'s Tollwut ist{' '}
          <span className="font-bold text-rose-700 dark:text-rose-300">abgelaufen</span> — bitte sofort auffrischen
        </>
      );
    }
    if (tollwutDays !== null && tollwutDays >= 0 && tollwutDays <= 30) {
      return (
        <>
          <span className="font-bold text-gray-900 dark:text-white">{dogName}</span> braucht eine{' '}
          <span className="font-bold text-amber-700 dark:text-amber-300">Tollwut-Auffrischung</span>, sonst läuft die
          Frist in {tollwutDays} {tollwutDays === 1 ? 'Tag' : 'Tagen'} ab
        </>
      );
    }
    if (status.missingDocuments.length > 0) {
      const first = status.missingDocuments[0]?.description || 'ein Dokument';
      const more = status.missingDocuments.length - 1;
      return (
        <>
          <span className="font-bold text-gray-900 dark:text-white">{dogName}</span> braucht noch{' '}
          <span className="font-bold text-amber-700 dark:text-amber-300">{first}</span>
          {more > 0 && ` (und ${more} weitere${more === 1 ? 's' : ''})`}
        </>
      );
    }
    if (completionPct < 100) {
      return (
        <>
          <span className="font-bold text-gray-900 dark:text-white">{dogName}</span>'s Profil ist zu{' '}
          <span className="font-bold text-amber-700 dark:text-amber-300">{completionPct} %</span> komplett
        </>
      );
    }
    return (
      <>
        <span className="font-bold text-primary-700 dark:text-primary-300">{dogName}</span> ist startklar — Profil
        komplett, alle Dokumente da
      </>
    );
  };

  const monthYear = new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <div
      className="min-h-screen font-sans text-gray-900 dark:text-slate-200"
      style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">
        {/* INTRO: Story + Stats */}
        {dogs.length > 0 && (
          <section className="pt-2 pb-6">
                <div className="text-[11px] uppercase tracking-[0.22em] text-primary-600/80 dark:text-primary-400/80 font-semibold mb-3">
                  {totalDogs} {totalDogs === 1 ? 'Pfote' : 'Pfoten'} · Stand {monthYear}
                </div>
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight mb-4">
                  Deine <span className="text-primary-600 dark:text-primary-400">{totalDogs === 1 ? 'Pfote.' : 'Pfoten.'}</span>
                </h1>
                <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
                  {dogs.map((dog, i) => (
                    <React.Fragment key={dog.hunde_id}>
                      {i > 0 && '. '}
                      {renderStorySnippet(dog)}
                    </React.Fragment>
                  ))}
                  .
                </p>
          </section>
        )}

        {/* DOGS LIST */}
        {dogs.length === 0 ? (
          <section className="pt-8 pb-12">
            <div
              className="rounded-3xl p-10 sm:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
              }}
            >
              <div
                className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5 bg-primary-500/10 border border-primary-500/30"
              >
                <DogIcon className="h-10 w-10 text-primary-600 dark:text-primary-400" />
              </div>
              <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                Noch keine Pfote registriert.
              </h4>
              <p className="text-gray-600 dark:text-slate-400 text-base mt-3 max-w-md mx-auto">
                Lege deinen ersten Hund an, um Trainings zu buchen und Tollwut-Impfung &
                Versicherung im Blick zu haben.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-primary-500 hover:bg-primary-400 text-slate-950 font-bold text-sm transition"
                >
                  <PlusIcon className="w-4 h-4" />
                  Hund hinzufügen
                </button>
              </div>
            </div>
          </section>
        ) : (
          <>
            <section className="space-y-6 pb-6">
              {dogs.map((dog) => (
                <DogCard key={dog.hunde_id} dog={dog} />
              ))}
            </section>
            <section className="pb-2 flex justify-center">
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-dashed border-primary-500/40 bg-primary-500/5 text-primary-300 hover:bg-primary-500/10 text-sm font-bold transition"
              >
                <PlusIcon className="w-4 h-4" />
                Weiteren Hund hinzufügen
              </button>
            </section>
          </>
        )}
      </div>

      {/* Modals */}
      <DogModal
        dog={editingDog}
        isOpen={showAddModal || !!editingDog}
        onClose={() => {
          setShowAddModal(false);
          setEditingDog(undefined);
        }}
        onSave={(message) => {
          loadDogs();
          if (message) showNotification(message, 'success');
        }}
        onDelete={editingDog ? () => {
          const name = editingDog.rufname || editingDog.hundename || 'diesen Hund';
          handleDeleteDog(editingDog.hunde_id, name);
        } : undefined}
      />

      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={
          confirmModal.variant === 'single'
            ? {
                text: 'OK',
                onPress: () => {
                  confirmModal.onConfirm();
                  setConfirmModal({ ...confirmModal, visible: false });
                },
              }
            : undefined
        }
        buttons={
          confirmModal.variant === 'two'
            ? {
                cancel: {
                  text: 'Abbrechen',
                  onPress: () => setConfirmModal({ ...confirmModal, visible: false }),
                },
                confirm: {
                  text: 'Bestätigen',
                  onPress: () => {
                    confirmModal.onConfirm();
                    setConfirmModal({ ...confirmModal, visible: false });
                  },
                },
              }
            : undefined
        }
      />
    </div>
  );
};

export default MyDogs;
